import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useContext } from 'react';
import SessionContext from '../../contexts/SessionContext';
import API_URL from '../../constants/environment';
import { DomainTimeseriesRequest, DomainTimeseriesResponse, DomainType, ReportingComparison, VendorGroupBy } from '../../types/reporting';

interface DomainTimeseriesInput extends DomainTimeseriesRequest {
    preset?: string;
    comparison?: ReportingComparison;
    includeComparison?: boolean;
}

const getDomainTimeseries = async (
    sessionId: string,
    input: DomainTimeseriesInput
): Promise<DomainTimeseriesResponse> => {
    const { data } = await axios.post(
        `${API_URL}/reporting/domain/timeseries`,
        input,
        { headers: { Authorization: sessionId } }
    );
    return data;
};

export const useDomainTimeseries = (
    domain: DomainType,
    startDate: string,
    endDate: string,
    options?: {
        domainId?: string;
        /** Compare mode: up to 5 entity ids (overrides domainId). */
        domainIds?: string[];
        granularity?: 'day' | 'week' | 'biweekly' | 'month';
        locationId?: string;
        preset?: string;
        comparison?: ReportingComparison;
        excludeEvs?: boolean;
        groupBy?: VendorGroupBy;
        /** When false, keep cache but do not fetch (inactive reporting tab). */
        enabled?: boolean;
    }
) => {
    const sessionContext = useContext(SessionContext);

    const {
        domainId,
        domainIds,
        granularity = 'week',
        locationId,
        preset,
        comparison,
        excludeEvs,
        groupBy,
        enabled = true,
    } = options || {};

    const domainIdsKey = domainIds?.length ? [...domainIds].sort().join(',') : undefined;

    return useQuery({
        queryKey: [
            'reporting',
            'domain-timeseries',
            domain,
            startDate,
            endDate,
            granularity,
            domainId,
            domainIdsKey,
            locationId,
            preset,
            comparison,
            excludeEvs,
            groupBy,
        ],
        queryFn: () => getDomainTimeseries(sessionContext?.sessionId ?? '', {
            domain,
            startDate,
            endDate,
            domainId: domainIds?.length ? undefined : domainId,
            domainIds: domainIds?.length ? domainIds : undefined,
            granularity,
            locationId,
            preset,
            comparison,
            includeComparison: !!comparison,
            excludeEvs,
            groupBy,
        }),
        enabled: enabled && !!sessionContext?.sessionId && !!startDate && !!endDate,
        staleTime: 1000 * 60 * 5,
        placeholderData: (previousData) => previousData,
    });
};

// Convenience hooks for specific domains
export const useLocationTimeseries = (
    startDate: string,
    endDate: string,
    options?: { domainId?: string; granularity?: 'day' | 'week' | 'biweekly' | 'month'; preset?: string; comparison?: ReportingComparison; }
) => useDomainTimeseries('location', startDate, endDate, options);

export const useVendorTimeseries = (
    startDate: string,
    endDate: string,
    options?: { domainId?: string; domainIds?: string[]; granularity?: 'day' | 'week' | 'biweekly' | 'month'; locationId?: string; preset?: string; comparison?: ReportingComparison; }
) => useDomainTimeseries('vendor', startDate, endDate, options);

export const useInsuranceTimeseries = (
    startDate: string,
    endDate: string,
    options?: { domainId?: string; granularity?: 'day' | 'week' | 'biweekly' | 'month'; locationId?: string; preset?: string; comparison?: ReportingComparison; }
) => useDomainTimeseries('insurance', startDate, endDate, options);

export const useOEMTimeseries = (
    startDate: string,
    endDate: string,
    options?: { domainId?: string; granularity?: 'day' | 'week' | 'biweekly' | 'month'; locationId?: string; preset?: string; comparison?: ReportingComparison; }
) => useDomainTimeseries('oem', startDate, endDate, options);
